import React, { Component } from 'react'
import uniq from 'lodash/uniq'
import { Modal, Button, Alert, Table, Checkbox, Input } from 'antd'
import { injectIntl, FormattedMessage } from 'react-intl'
import { Query } from '@apollo/client/react/components'
import difference from 'lodash/difference'
import { SearchOutlined } from '@ant-design/icons'

import Loading from '../../components/LoadingPage'
import * as Utils from '../../components/Utils'
import { isMatch } from '../../components/utils/searchUtils'
import _ from 'lodash'

class SelectZones extends Component {
	state = {
		loading: false,
		loadingSync: false,
		selected: this.props.selected || [],
		zoneFilter: ''
	}

	handleSubmit = async () => {
		this.setState({ loading: true })
		await this.props.onSave(
			this.state.selected.filter((d) => d.indexOf('device-') === -1),
			difference(this.props.selected, this.state.selected).filter(
				(d) => d.indexOf('device-') === -1
			),
			false
		)
		this.setState({ loading: false })
		this.props.onClose()
	}

	handleSubmitSync = async () => {
		this.setState({ loadingSync: true })
		await this.props.onSave(
			this.state.selected.filter((d) => d.indexOf('device-') === -1),
			difference(this.props.selected, this.state.selected).filter(
				(d) => d.indexOf('device-') === -1
			),
			true
		)
		this.setState({ loadingSync: false })
		this.props.onClose()
	}

	recordMatchSearchQuery(query, record) {
		// allow to match query "deauville hamman" where deauville is
		// found in the device name and hamman in the zone name
		return isMatch([record.name, record?.parentName], query)
	}

	filterZonesAndDevicesBySearchQuery(records, query) {
		// when we filter nested records, we dont want to assign empty arrays
		if (_.isEmpty(records)) {
			return undefined
		}

		const filteredRecords = []
		for (let record of records) {
			const matchingChildren = this.filterZonesAndDevicesBySearchQuery(
				record.children,
				query
			)
			if (
				!_.isEmpty(matchingChildren) ||
				this.recordMatchSearchQuery(query, record)
			) {
				filteredRecords.push({
					...record,
					children: matchingChildren
				})
			}
		}

		return filteredRecords
	}

	formatData = (data) => {
		let devices = []
		let deviceIds = []

		for (let team of data) {
			for (let d of team.devices) {
				if (deviceIds.indexOf(d._id) === -1) {
					let device
					if (d.zones.length === 1) {
						let zone = d.zones[0]
						device = {
							key: zone._id,
							name: d.nickname || d.name,
							// TODO: the ?. is a quickfix for eng-2781 but must be fixed more thoroughly later
							messageProgramsEnabled:
								team.messagesEnabled?.value ||
								team.parentMessagesEnabled,
							type: d.type
						}
						if (zone.program) {
							device.program = zone.program.name
						}
						if (zone.messagePrograms) {
							device.messagePrograms = (
								<ul>
									{zone.messagePrograms.map((mp) => (
										<li key={mp._id}>{mp.name}</li>
									))}
								</ul>
							)
						}
					} else {
						device = {
							key: `device-${d._id}`,
							name: d.nickname || d.name,
							// TODO: the ?. is a quickfix for eng-2781 but must be fixed more thoroughly later
							messageProgramsEnabled:
								team.messagesEnabled?.value ||
								team.parentMessagesEnabled,
							type: d.type,
							children: d.zones.map((z) => ({
								key: z._id,
								name: z.name,
								// to allow the search to take the parent device name into account
								parentName: d.nickname || d.name,
								program: z.program ? z.program.name : null,
								type: d.type,
								messageProgramsEnabled:
									team.messagesEnabled?.value ||
									team.parentMessagesEnabled,
								messagePrograms:
									z.messagePrograms ?
										<ul>
											{z.messagePrograms.map((mp) => (
												<li key={mp._id}>{mp.name}</li>
											))}
										</ul>
									:	null
							}))
						}
					}
					devices.push(device)
					deviceIds.push(d._id)
				}
			}
		}

		// if there is a search filter, we apply it now to search in all the items and subitems
		// antd table filtering only works on top level items otherwise
		if (_.isEmpty(this.state.zoneFilter)) {
			return devices
		}

		return this.filterZonesAndDevicesBySearchQuery(
			devices,
			this.state.zoneFilter
		)
	}

	render() {
		const { intl, title, onClose, teams, property } = this.props
		const rowSelection = {
			onChange: (selectedRowKeys) => {
				this.setState({ selected: selectedRowKeys })
			},
			selectedRowKeys: this.state.selected,
			getCheckboxProps: (record) => {
				if (this.props.isRowDisabled) {
					const disabled = this.props.isRowDisabled(record)
					if (disabled) {
						return { disabled: true }
					}
				}

				// those with key "device-" are parent rows for devices with multiple zones
				if (record.key.indexOf('device-') > -1) {
					return { disabled: true }
				}
				return null
			}
		}

		const columns = [
			{
				title: intl.formatMessage({ id: 'name' }),
				dataIndex: 'name',
				key: 'name',
				filteredValue: [this.state.zoneFilter],
				filterDropdown: ({
					setSelectedKeys,
					selectedKeys,
					confirm
				}) => (
					<div
						style={{ padding: 8 }}
						onKeyDown={(e) => {
							e.stopPropagation()
							// if the user presses escape then clear and close the dropdown
							if (e.key === 'Escape') {
								this.setState({
									zoneFilter: ''
								})
								confirm()
							}
						}}
					>
						<Input
							ref={(node) => {
								this.searchInput = node
							}}
							placeholder={intl.formatMessage({
								id: 'tables.filters.filterByDevice'
							})}
							style={{
								display: 'block'
							}}
							onChange={(e) => {
								this.setState({
									zoneFilter: e.target.value
								})
							}}
							onPressEnter={() => {
								// close the dropdown on pressing enter
								confirm()
							}}
						/>
					</div>
				),
				filterIcon: (filtered) => (
					<div
						style={{
							display: 'inline-flex',
							alignItems: 'center'
						}}
					>
						{this.state.zoneFilter && (
							<div style={{ marginRight: 8 }}>
								<FormattedMessage
									id="tables.filters.showingResults"
									values={{
										filter: this.state.zoneFilter,
										i: (chunks) => <i>{chunks}</i>
									}}
								/>
							</div>
						)}
						<SearchOutlined
							style={{ color: filtered ? '#1677ff' : undefined }}
						/>
					</div>
				),
				onFilter: () => {
					// the filtering is done in the formatData function to support zones
					// displayed as nested items in the table (for devices with multiple zones)
					return true
				},
				onFilterDropdownVisibleChange: (visible) => {
					if (visible) {
						// focus on the input after the dropdown opens
						setTimeout(() => {
							this.searchInput.focus()
						}, 0)
					}
				}
			},
			{
				title: intl.formatMessage({ id: property }),
				dataIndex: property,
				key: property
			}
		]

		return (
			<Modal
				visible={true}
				title={title}
				onCancel={onClose}
				width="80vw"
				bodyStyle={{ padding: 0 }}
				footer={[
					<Button
						key="save"
						type="default"
						onClick={this.handleSubmit}
						loading={this.state.loading}
					>
						<FormattedMessage id="actions.save" />
					</Button>,
					<Button
						key="saveSync"
						type="primary"
						onClick={this.handleSubmitSync}
						loading={this.state.loadingSync}
					>
						<FormattedMessage id="devices.player.changePrograms.saveAndSync" />
					</Button>
				]}
			>
				<Query query={this.props.query} variables={{ teams }}>
					{({ loading, error, data }) => {
						if (data && data.teams) {
							return (
								<Table
									columns={columns}
									pagination={false}
									rowSelection={rowSelection}
									dataSource={this.formatData(data.teams)}
									defaultExpandAllRows={true}
									onFilterZones={(zoneFilter) => {
										this.setState({ zoneFilter })
									}}
								/>
							)
						}
						if (loading) {
							return <Loading />
						}
						if (error) {
							return (
								<Alert
									message={intl.formatMessage({
										id: 'error'
									})}
									description={Utils.displayGraphQLErrors(
										error
									)}
									type="error"
									showIcon
								/>
							)
						}
					}}
				</Query>
			</Modal>
		)
	}
}

export default injectIntl(SelectZones)
